import { Icon } from "@/components/atoms";
import ShimmerTableRow from "@/components/atoms/ShimmerTableRow";
import DataTableBody from "./DataTableBody";
import DataTableFilterRow from "./DataTableFilterRow";
import DataTablePagination from "./DataTablePagination";
import DataTableHead from "./DataTableHead";
import DataTableToolbar from "./DataTableToolbar";
import SelectionSummary from "./SelectionSummary";
import EmptyState from "@/components/molecules/EmptyState/EmptyState";
import { useCustomDataTable } from '@/hooks/useCustomDataTable-hook';

import {
  Button,
  Paper,
  Table,
  TableContainer,
  TableHead,
} from "@mui/material";
import 'react-datepicker/dist/react-datepicker.css';
import "./CustomDataTable.scss";


const CustomDataTable = ({
  columns = [],
  records = [],
  isLoading = false,
  pageSize = 10,
  totalRecords = 0,
  setPageIndex = () => { },
  handleSorting = () => { },
  setSearchArry = () => { },
  onResetAll = () => { },
  onRowSelect = () => { },
  onDownload = () => { },
  showDownload = false,
  isSelectAll = false,
  onNameClick = () => { },
  pageIndex = 0,
  showCreateNew = false,
  onCreateNew = null,
  toolbarActions = [],
  initialSortConfig,
  initialRowSelection = [],
}) => {
  const {
    dateRanges,
    singleDates,
    openDatePicker,
    selectedRows,
    sortConfig,
    filter,
    totalPages,
    setOpenDatePicker,
    handleResetAll,
    handleSort,
    handleSelectAll,
    handleRowSelect,
    handleFilterChange,
    handleDateRangeChange,
    handleSingleDateChange,
  } = useCustomDataTable({
    columns,
    initialSortConfig,
    records,
    pageSize,
    onResetAll,
    onRowSelect,
    handleSorting,
    setSearchArry,
    setPageIndex,
    isSelectAll,
    totalRecords,
    pageIndex,
    initialRowSelection,
  });



  const CustomInput = ({ value = '', onClick, column, sx }) => {
    if (!column?.name_of_db_column) return null;

    const dateRange = dateRanges[column.name_of_db_column] || {};
    const { startDate, endDate } = dateRange;
    const showCross = startDate && endDate;
    const showCalendar = !showCross;

    const buttonText = startDate && endDate
      ? `${startDate.toLocaleDateString()} - ${endDate.toLocaleDateString()}`
      : startDate
        ? `${startDate.toLocaleDateString()} - End Date`
        : value || column.label_of_column

    return (
      <Button
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          setOpenDatePicker(column.name_of_db_column);
          onClick && onClick(e);
        }}
        variant="outlined"
        size="small"
        fullWidth
        sx={{ ...sx, textTransform: 'none' }}
        style={{ borderColor: 'rgba(255, 255, 255, 0.23)', color: showCalendar ? 'rgba(255, 255, 255, 0.23)' : "#EFEFEF" }}
      >
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", width: '100%', }}>
          <div style={{ fontSize: '12px' }}>{buttonText}</div>
          {showCalendar ? <Icon icon="calendar_today" size="small" /> : null}
        </div>
      </Button>
    );
  };

  const SingleDateCustomInput = ({ value = '', onClick, column, sx }) => {
    if (!column?.name_of_db_column) return null;

    const selectedDate = singleDates[column.name_of_db_column];
    const showCross = selectedDate != null;
    const showCalendar = !showCross;

    const buttonText = selectedDate
      ? `${selectedDate.toLocaleDateString()}`
      : value || column.label_of_column

    return (
      <Button
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          setOpenDatePicker(column.name_of_db_column);
          onClick && onClick(e);
        }}
        variant="outlined"
        size="small"
        fullWidth
        sx={{ ...sx, textTransform: 'none' }}
        style={{ borderColor: 'rgba(255, 255, 255, 0.23)', color: showCalendar ? 'rgba(255, 255, 255, 0.23)' : "#EFEFEF" }}
      >
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", width: '100%', }}>
          <div style={{ fontSize: '12px' }}>{buttonText}</div>
          {showCalendar ? <Icon icon="calendar_today" size="small" /> : null}
        </div>
      </Button>
    );
  };

  return (
    <div>
      <DataTableToolbar
        onResetAll={handleResetAll}
        selectedRowsCount={selectedRows?.length || 0}
        toolbarActions={[
          ...toolbarActions,
          ...(showCreateNew ? [{
            id: 'create-new',
            label: 'Create New',
            onClick: onCreateNew,
            icon: <Icon icon="add" size="small" />,
            variant: "secondary",
            isDark: true
          }] : []),
          ...(showDownload ? [{
            id: 'download',
            label: 'Download',
            onClick: onDownload,
            icon: <Icon icon="download" size="small" />,
            variant: "outlined",
            isDark: true,
            requiresSelection: true
          }] : [])
        ]}
      />
      <SelectionSummary selectedCount={selectedRows?.length || 0} />
      <TableContainer
        component={Paper}
        className={`table-scroll-container${records.length > 5 ? ' table-scroll-container--minheight' : ''}`}
      >
        <Table className="dark-mode-table table-min-width">
          <TableHead>
            <DataTableHead
              columns={columns}
              sortConfig={sortConfig}
              handleSort={handleSort}
              isSelectAll={isSelectAll}
              records={records}
              selectedRows={selectedRows}
              handleSelectAll={handleSelectAll}
            />
            <DataTableFilterRow
              columns={columns}
              dateRanges={dateRanges}
              singleDates={singleDates}
              openDatePicker={openDatePicker}
              filter={filter}
              setOpenDatePicker={setOpenDatePicker}
              handleDateRangeChange={handleDateRangeChange}
              handleSingleDateChange={handleSingleDateChange}
              handleFilterChange={handleFilterChange}
              CustomInput={CustomInput}
              SingleDateCustomInput={SingleDateCustomInput}
            />
          </TableHead>
          <DataTableBody
            columns={columns}
            records={records}
            isLoading={isLoading}
            selectedRows={selectedRows}
            handleRowSelect={handleRowSelect}
            handleResetAll={handleResetAll}
            onNameClick={onNameClick}
            EmptyState={EmptyState}
            ShimmerTableRow={ShimmerTableRow}
          />
        </Table>
      </TableContainer>
      <div className="sticky-pagination">
        <DataTablePagination
          totalPages={Number.isInteger(totalPages) && totalPages > 0 ? totalPages : 1}
          page={Number.isInteger(pageIndex) && pageIndex >= 0 ? pageIndex + 1 : 1}
          handlePageChange={(value) => {
            setPageIndex(value - 1);
          }}
        />
      </div>

    </div>
  );
}

export default CustomDataTable;
